'use client';

import * as React from 'react';
import { useLocale } from 'next-intl';
import { cn } from '@/lib/utils';
import { Typography } from '@/components/ui/typography';
import { Pill } from '@/components/ui/pill';

/** Page-title row fetched on the server so the banner renders in the initial HTML. */
export interface HeroBannerPageTitle {
    title?: string | null;
    pagePill?: string | null;
    sectionSubtitle?: string | null;
}

interface HeroBannerProps {
    page?: string;
    featureIdentifier?: string;
    title?: string;
    description?: string;
    pill?: string;
    image?: string;
    className?: string;
    overlayClassName?: string;
    /** SSR: pre-fetched page-titles row (skips the client fetch when provided) */
    initialPageTitle?: HeroBannerPageTitle | null;
}

export function HeroBanner({ page, featureIdentifier, title: initialTitle, description: initialDescription, pill: initialPill, image, className, overlayClassName, initialPageTitle }: HeroBannerProps) {
    const locale = useLocale();
    // A server-provided page title lets the banner render its real copy in the first paint;
    // without one we fall back to the props and fill in from the API on the client.
    const hasServerPageTitle = !!(page && initialPageTitle);
    const [title, setTitle] = React.useState(
        hasServerPageTitle ? (initialPageTitle?.title || initialTitle || '') : (featureIdentifier ? '' : (initialTitle || ''))
    );
    const [description, setDescription] = React.useState(
        hasServerPageTitle ? (initialPageTitle?.sectionSubtitle || initialDescription || '') : (featureIdentifier ? '' : (initialDescription || ''))
    );
    const [pill, setPill] = React.useState(
        hasServerPageTitle ? (initialPageTitle?.pagePill || initialPill || '') : (initialPill || '')
    );
    const [isLoading, setIsLoading] = React.useState(!hasServerPageTitle && !!(page || featureIdentifier));

    // If featureIdentifier is provided, fetch from features API; otherwise fetch from page-titles
    React.useEffect(() => {
        if (hasServerPageTitle) {
            // Already rendered from server data - nothing to fetch.
            setTitle(initialPageTitle?.title || initialTitle || '');
            setDescription(initialPageTitle?.sectionSubtitle || initialDescription || '');
            setPill(initialPageTitle?.pagePill || initialPill || '');
            setIsLoading(false);
            return;
        }
        if (featureIdentifier) {
            // Fetch from features API when featureIdentifier is provided
            // ONLY use API data, no fallback to props/translations
            const fetchHeroFromFeature = async () => {
                try {
                    setIsLoading(true);
                    const res = await fetch(`/api/features?identifier=${encodeURIComponent(featureIdentifier)}&locale=${encodeURIComponent(locale)}`, {
                        cache: 'no-store',
                    });
                    if (!res.ok) {

                        // Use initial props as fallback if API fails
                        if (initialTitle && initialTitle.trim() !== '') {
                            setTitle(initialTitle);
                        }
                        if (initialDescription && initialDescription.trim() !== '') {
                            setDescription(initialDescription);
                        }
                        setIsLoading(false);
                        return;
                    }
                    const data = await res.json();
                    if (!data) {

                        // Use initial props as fallback if no data
                        if (initialTitle && initialTitle.trim() !== '') {
                            setTitle(initialTitle);
                        }
                        if (initialDescription && initialDescription.trim() !== '') {
                            setDescription(initialDescription);
                        }
                        setIsLoading(false);
                        return;
                    }

                    // Warn if locale doesn't match
                    if (data.locale && data.locale !== locale) {
                        // Locale mismatch detected
                    }

                    // Use API data if available, otherwise fallback to initial props
                    if (data.title && data.title.trim() !== '') {
                        setTitle(data.title);
                    } else if (initialTitle && initialTitle.trim() !== '') {
                        setTitle(initialTitle); // Fallback to prop if API has no title
                    } else {
                        setTitle(''); // Clear if no API data and no prop
                    }

                    if (data.description && data.description.trim() !== '') {
                        setDescription(data.description);
                    } else if (initialDescription && initialDescription.trim() !== '') {
                        setDescription(initialDescription); // Fallback to prop if API has no description
                    } else {
                        setDescription(''); // Clear if no API data and no prop
                    }

                    // Pill can use initial if provided, but title/description must come from API
                    if (initialPill) {
                        setPill(initialPill);
                    }
                } catch (error) {

                    // Use initial props as fallback on error
                    if (initialTitle && initialTitle.trim() !== '') {
                        setTitle(initialTitle);
                    }
                    if (initialDescription && initialDescription.trim() !== '') {
                        setDescription(initialDescription);
                    }
                } finally {
                    setIsLoading(false);
                }
            };
            fetchHeroFromFeature();
        } else if (page) {
            // Fetch from page-titles API when page prop is provided
            const fetchHero = async () => {
                try {
                    setIsLoading(true);
                    const res = await fetch(`/api/page-titles?page=${encodeURIComponent(page)}&locale=${encodeURIComponent(locale)}`, {
                        cache: 'no-store',
                    });
                    if (!res.ok) {
                        setIsLoading(false);
                        return;
                    }
                    const data = await res.json();
                    if (!data) {
                        setIsLoading(false);
                        return;
                    }

                    setTitle(data.title || initialTitle || '');
                    setDescription(data.sectionSubtitle || initialDescription || '');
                    setPill(data.pagePill || initialPill || '');
                } catch (error) {

                } finally {
                    setIsLoading(false);
                }
            };
            fetchHero();
        } else {
            // No API fetch needed, content comes from props
            setIsLoading(false);
        }
    }, [page, featureIdentifier, locale, initialTitle, initialDescription, initialPill, hasServerPageTitle, initialPageTitle]);

    // When featureIdentifier or page prop exists, use state (which gets updated from API)
    // Otherwise, use provided props directly
    const displayTitle = (featureIdentifier || page) ? title : (initialTitle || '');
    const displayDescription = (featureIdentifier || page) ? description : (initialDescription || '');
    const displayPill = (featureIdentifier || page) ? pill : (initialPill || '');
    return (
        <section className={cn('relative overflow-hidden', className)}>
            {image ? (
                <>
                    <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{
                            backgroundImage: `url(${image})`,
                        }}
                    />
                    <div className="absolute inset-0 bg-black/40 dark:bg-black/60 pointer-events-none" />
                </>
            ) : (
                <>
                    <div
                        className={cn(
                            "absolute inset-0 pointer-events-none bg-[url('/images/common/page-header-bg.svg')] bg-cover bg-center",
                            overlayClassName
                        )}
                    />
                    <div className="absolute inset-0 bg-white/20 dark:bg-[#0b1223]/40 pointer-events-none" />
                </>
            )}
            <div className="absolute -right-24 -top-16 h-48 w-48 rounded-full bg-indigo-900/30 blur-3xl pointer-events-none" />
            <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-slate-800/40 blur-3xl pointer-events-none" />
            <div className="absolute right-10 top-12 h-10 w-36 rounded-full bg-indigo-700/40 blur-lg pointer-events-none" />
            <div className="absolute left-10 bottom-16 h-10 w-36 rounded-full bg-blue-500/35 blur-lg pointer-events-none" />

            <div className="relative container mx-auto  px-4 py-16 sm:py-20 lg:py-24">
                {isLoading ? (
                    <div className="max-w-4xl space-y-4 text-left">
                        {/* Pill skeleton */}
                        <div className="h-8 w-32 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
                        {/* Title skeleton */}
                        <div className="space-y-2">
                            <div className="h-8 sm:h-10 lg:h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse w-3/4" />
                            <div className="h-8 sm:h-10 lg:h-12 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse w-1/2" />
                        </div>
                        {/* Description skeleton */}
                        <div className="space-y-2 pt-2">
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-5/6" />
                            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-4/6" />
                        </div>
                    </div>
                ) : (
                    <div className="max-w-4xl space-y-4 text-left text-gray-900 dark:text-white">
                        {displayPill ? (
                            <Pill
                                variant="left"
                                showDot
                                dotColor="text-blue-400"
                                className="text-gray-900 dark:text-white tracking-[0.18em]"
                            >
                                {displayPill}
                            </Pill>
                        ) : null}
                        <Typography
                            variant="h1"
                            className="!text-2xl sm:!text-3xl lg:!text-4xl font-semibold leading-tight drop-shadow-sm"
                        >
                            {displayTitle}
                        </Typography>
                        <Typography variant="p" className="text-base sm:text-lg text-gray-700 dark:text-slate-200 leading-relaxed">
                            {displayDescription}
                        </Typography>
                    </div>
                )}
            </div>
        </section>
    );
}

